import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Button, Col, Input, Row, Spinner } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { useListParams } from "hooks/useListParams"
import {
  useDailyReports,
  useGenerateDailyReport,
} from "hooks/dailyReportHook"
import { downloadDailyReportPdf } from "api/dailyReportApi"
import ReportViewModal from "./ReportViewModal"

const todayIso = () => new Date().toISOString().slice(0, 10)

const PERIOD_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
]

const DailyReportList = () => {
  document.title = "Daily Reports"

  const { params, setPage } = useListParams({ page: 1 })
  const [filters, setFilters] = useState({ from: "", to: "", period: "" })
  const [viewReport, setViewReport] = useState(null)
  const [genDate, setGenDate] = useState(todayIso())
  const [pdfLoadingId, setPdfLoadingId] = useState(null)

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: 20,
      from: filters.from || undefined,
      to: filters.to || undefined,
      period: filters.period || undefined,
    }),
    [params, filters]
  )

  const { data: resp, isLoading, isRefetching, refetch } = useDailyReports(queryParams)
  const rows = resp?.data?.data ?? []
  const paginationInfo = resp?.data

  const generateMutation = useGenerateDailyReport()

  const handleGenerate = async () => {
    try {
      await generateMutation.mutateAsync({ date: genDate })
      toast.success("Report generated")
      refetch()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Generation failed")
    }
  }

  const handleDownloadPdf = async report => {
    setPdfLoadingId(report.id)
    try {
      const blob = await downloadDailyReportPdf(report.id)
      window.open(URL.createObjectURL(blob), "_blank", "noopener")
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not open PDF")
    } finally {
      setPdfLoadingId(null)
    }
  }

  const columns = useMemo(
    () => [
      { header: "Date", accessorKey: "report_date" },
      {
        header: "Period",
        cell: ({ row }) => (
          <span className="text-capitalize">{row.original.period}</span>
        ),
      },
      {
        header: "Generated",
        cell: ({ row }) =>
          row.original.generated_at
            ? new Date(row.original.generated_at).toLocaleString()
            : "—",
      },
      {
        header: "PDF",
        cell: ({ row }) => (row.original.pdf_path ? "Ready" : "—"),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const isPdfLoading = pdfLoadingId === row.original.id
          return (
            <div className="d-flex gap-1">
              <Button size="sm" color="primary" onClick={() => setViewReport(row.original)}>
                View
              </Button>
              <Button
                size="sm"
                color="danger"
                outline
                disabled={isPdfLoading}
                onClick={() => handleDownloadPdf(row.original)}
              >
                {isPdfLoading ? <Spinner size="sm" /> : "PDF"}
              </Button>
            </div>
          )
        },
      },
    ],
    [pdfLoadingId]
  )

  return (
    <PageWrapper title="Daily Reports" breadcrumbItem="Daily Reports">
      <Row className="mb-3 g-2 align-items-end">
        <Col md={2}>
          <label className="form-label">From</label>
          <Input
            type="date"
            value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
          />
        </Col>
        <Col md={2}>
          <label className="form-label">To</label>
          <Input
            type="date"
            value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
          />
        </Col>
        <Col md={2}>
          <label className="form-label">Period</label>
          <Select
            isClearable
            options={PERIOD_OPTIONS}
            value={PERIOD_OPTIONS.find(o => o.value === filters.period) || null}
            onChange={opt => setFilters(f => ({ ...f, period: opt?.value || "" }))}
          />
        </Col>
        <Col md={6} className="text-end">
          <div className="d-inline-flex gap-2 align-items-end">
            <div>
              <label className="form-label">Generate for date</label>
              <Input type="date" value={genDate} onChange={e => setGenDate(e.target.value)} />
            </div>
            <Button
              color="success"
              onClick={handleGenerate}
              disabled={generateMutation.isPending}
            >
              {generateMutation.isPending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Generating...
                </>
              ) : (
                "Generate"
              )}
            </Button>
          </div>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={rows}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      {viewReport && (
        <ReportViewModal
          isOpen={Boolean(viewReport)}
          toggle={() => setViewReport(null)}
          report={viewReport}
        />
      )}
    </PageWrapper>
  )
}

export default DailyReportList
